import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Zap, 
  BarChart3, 
  Video, 
  Play, 
  ArrowRight, 
  Check,
  ChevronDown,
  Clock,
  Store,
  Youtube,
  Instagram,
  Menu,
  X,
  Star,
  Wand2,
  Calendar,
  Music,
  Film,
  Users
} from 'lucide-react';
import './LandingPage.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Constants
const features = [
  {
    id: "0",
    icon: <Wand2 />,
    caption: "AI-Powered Creation",
    title: "Generate Videos, Shorts & Music",
    text: "Create professional videos, YouTube Shorts, and even AI music videos with just a few clicks. V Lumina AI handles scripting, editing, and optimization automatically.",
    button: { title: "See how it works" }
  },
  {
    id: "1",
    icon: <Calendar />,
    caption: "Set It & Forget It",
    title: "Automated Content on Autopilot",
    text: "Schedule AI jobs to run in the background. V Lumina AI creates and posts content regularly based on your settings — so you can focus on running your business.",
    button: { title: "Start free trial" }
  }
];

const details = [
  { id: "0", icon: <Video size={24} />, title: "Videos & Shorts" },
  { id: "1", icon: <Music size={24} />, title: "AI Music Videos" },
  { id: "2", icon: <Youtube size={24} />, title: "Multi-Platform" },
  { id: "3", icon: <Clock size={24} />, title: "Scheduled Automation" }
];

// Base plans (channel limits)
const plans = [
  {
    id: "0",
    title: "Free",
    priceMonthly: 0,
    priceYearly: 0,
    caption: "Get started free",
    features: ["1 YouTube Channel", "Channel Dashboard", "Basic Analytics", "Community Support"],
  },
  {
    id: "1",
    title: "Starter",
    priceMonthly: 3,
    priceYearly: 2,
    caption: "For growing creators",
    features: ["3 YouTube Channels", "Advanced Analytics", "Priority Support", "All Free features"],
  },
  {
    id: "2",
    title: "Creator",
    priceMonthly: 7,
    priceYearly: 5,
    caption: "For serious YouTubers",
    features: ["5 YouTube Channels", "Team Collaboration", "API Access", "All Starter features"],
  },
  {
    id: "3",
    title: "Business",
    priceMonthly: 15,
    priceYearly: 12,
    caption: "For agencies & teams",
    features: ["10 YouTube Channels", "White-label Options", "Dedicated Support", "All Creator features"],
  }
];

// Tool add-ons
const toolAddons = [
  {
    id: "gen_pro",
    title: "Gen Pro",
    price: 15,
    icon: "🎬",
    description: "AI-powered YouTube playlist creator",
    features: [
      "Generate AI music tracks",
      "Auto-create thumbnails",
      "Batch video creation",
      "Direct YouTube upload",
      "Perfect for music compilations"
    ]
  },
  {
    id: "storyteller",
    title: "Storyteller",
    price: 19,
    icon: "📖",
    description: "AI video storytelling engine",
    features: [
      "Research any topic with AI",
      "Generate video scripts",
      "AI-generated visuals",
      "Text-to-speech narration",
      "Perfect for educational content"
    ]
  }
];

const faq = [
  { id: "0", question: "What kind of content can V Lumina AI create?", answer: "V Lumina AI generates full videos, YouTube Shorts, and AI music videos. From scripting to editing to optimization — it handles the entire content creation process automatically." },
  { id: "1", question: "Which platforms does it support?", answer: "Currently we support YouTube with direct integration. TikTok and Instagram integrations are coming soon. You can also download content to post anywhere." },
  { id: "2", question: "How does the automation work?", answer: "Set up an AI job with your preferences — content type, posting schedule, topics, and style. V Lumina AI runs in the background, creating and posting content automatically based on your settings." },
  { id: "3", question: "Is this good for local businesses?", answer: "Absolutely! Local businesses use V Lumina AI to maintain an active social presence without hiring expensive marketing agencies. It's like having a content team that works 24/7." },
  { id: "4", question: "Can YouTubers use this to grow their channels?", answer: "Yes! Many creators use V Lumina AI to automate their content pipeline, generate video ideas, create Shorts from long-form content, and maintain consistent posting schedules." },
  { id: "5", question: "Do you offer a free trial?", answer: "Yes! Start with our free Starter plan to try it out, or upgrade to Creator/Business for full features. No credit card required to get started." }
];

const testimonials = [
  { id: "0", name: "Alex Rivera", role: "YouTuber, 50K subs", comment: "V Lumina AI automated my Shorts pipeline. I went from posting once a week to daily — and my channel grew 3x in 2 months." },
  { id: "1", name: "Sarah Chen", role: "Dental Practice Owner", comment: "We set up automated videos for our practice. Now we have a consistent YouTube presence without lifting a finger." },
  { id: "2", name: "Marcus Johnson", role: "Music Producer", comment: "The AI music video feature is insane. I can create visualizers for my tracks in minutes, not hours." },
  { id: "3", name: "Green Thumb Landscaping", role: "Local Business", comment: "Our before/after videos run on autopilot. Customers find us through YouTube now — it's like free advertising." }
];

// Components
const FaqItem = ({ item, index, activeIndex, setActiveIndex }) => {
  const isActive = activeIndex === index;
  
  return (
    <div className="faq-item">
      <button 
        className={`faq-question ${isActive ? 'active' : ''}`}
        onClick={() => setActiveIndex(isActive ? null : index)}
      >
        <span>{item.question}</span>
        <div className={`faq-icon ${isActive ? 'active' : ''}`}>
          <ChevronDown size={20} />
        </div>
      </button>
      <div className={`faq-answer ${isActive ? 'active' : ''}`}>
        <p>{item.answer}</p>
      </div>
    </div>
  );
};

const TestimonialCard = ({ item }) => (
  <div className="testimonial-card">
    <div className="testimonial-stars">
      {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#FF9068" color="#FF9068" />)}
    </div>
    <p className="testimonial-comment">"{item.comment}"</p>
    <div className="testimonial-author">
      <div className="testimonial-avatar">{item.name.charAt(0)}</div>
      <div>
        <div className="testimonial-name">{item.name}</div>
        <div className="testimonial-role">{item.role}</div>
      </div>
    </div>
  </div>
);

function LandingPage() {
  const navigate = useNavigate();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [monthly, setMonthly] = useState(true);
  const [activeFaq, setActiveFaq] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const handleScroll = () => setHasScrolled(window.scrollY > 32);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('vlumina_user');
    const token = localStorage.getItem('vlumina_token');
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('vlumina_user');
        localStorage.removeItem('vlumina_token');
      }
    }
  }, []);

  const handleGetStarted = () => user ? navigate('/dashboard') : navigate('/signup');
  const handleSignIn = () => navigate('/signin');
  const handleGoToDashboard = () => navigate('/dashboard');
  const handleLogout = () => {
    localStorage.removeItem('vlumina_user');
    localStorage.removeItem('vlumina_token');
    setUser(null);
  };

  const scrollToSection = (id) => {
    setIsMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="xora-landing">
      {/* Header */}
      <header className={`xora-header ${hasScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <a className="header-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Sparkles className="logo-icon" />
            <span>V Lumina AI</span>
          </a>

          <nav className={`header-nav ${isMenuOpen ? 'open' : ''}`}>
            <button className="nav-link" onClick={() => scrollToSection('features')}>Features</button>
            <button className="nav-link" onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button className="nav-link" onClick={() => scrollToSection('faq')}>FAQ</button>
            <button className="nav-link" onClick={() => scrollToSection('testimonials')}>Reviews</button>
          </nav>

          <div className="header-actions">
            {user ? (
              <>
                <button className="btn-glow" onClick={handleGoToDashboard}>
                  Dashboard
                </button>
                <button className="btn-ghost" onClick={handleLogout}>Logout</button>
              </>
            ) : (
              <>
                <button className="btn-ghost" onClick={handleSignIn}>Sign In</button>
                <button className="btn-glow" onClick={handleGetStarted}>
                  Get Started
                </button>
              </>
            )}
          </div>

          <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="xora-hero">
        <div className="hero-bg-glow"></div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-caption">
              <Video size={14} />
              <span>ONLINE CONTENT AUTOMATION</span>
            </div>
            <h1 className="hero-title">
              Create Content While You Sleep
            </h1>
            <p className="hero-text">
              V Lumina AI generates videos, Shorts, and music videos automatically. 
              Connect your YouTube, set your schedule, and let AI handle the rest.
            </p>
            <button className="btn-hero" onClick={handleGetStarted}>
              <span className="btn-hero-inner">
                <Zap size={20} />
                <span>Try it free</span>
              </span>
              <span className="btn-hero-glow"></span>
            </button>
          </div>

          <div className="hero-image">
            <div className="hero-image-glow"></div>
            <div className="hero-dashboard">
              <div className="dash-header">
                <div className="dash-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <span className="dash-title">V Lumina AI</span>
              </div>
              <div className="dash-content">
                <div className="dash-sidebar">
                  <div className="dash-channel">
                    <div className="dash-avatar">
                      <Youtube size={16} />
                    </div>
                    <div className="dash-info">
                      <span className="dash-channel-name">My Channel</span>
                      <span className="dash-channel-subs">12.4K subs</span>
                    </div>
                  </div>
                  <div className="dash-nav">
                    <div className="dash-nav-item active">
                      <Video size={14} />
                      <span>Video Generator</span>
                    </div>
                    <div className="dash-nav-item">
                      <Film size={14} />
                      <span>Shorts Creator</span>
                    </div>
                    <div className="dash-nav-item">
                      <Music size={14} />
                      <span>Music Videos</span>
                    </div>
                    <div className="dash-nav-item">
                      <Calendar size={14} />
                      <span>Automation</span>
                    </div>
                  </div>
                </div>
                <div className="dash-main">
                  <div className="dash-section-title">
                    <Wand2 size={16} />
                    <span>AI Video Generator</span>
                  </div>
                  <div className="dash-card">
                    <div className="dash-card-header">
                      <span className="dash-card-label">Topic</span>
                    </div>
                    <div className="dash-input">5 Tips for Small Business Marketing</div>
                  </div>
                  <div className="dash-card">
                    <div className="dash-card-header">
                      <span className="dash-card-label">Style</span>
                    </div>
                    <div className="dash-tags">
                      <span className="dash-tag active">Educational</span>
                      <span className="dash-tag">Tutorial</span>
                      <span className="dash-tag">Vlog</span>
                    </div>
                  </div>
                  <div className="dash-generate-btn">
                    <Sparkles size={16} />
                    <span>Generate Video</span>
                  </div>
                  <div className="dash-status">
                    <div className="dash-status-dot"></div>
                    <span>AI Job Running: 3 videos scheduled this week</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="xora-features">
        <div className="features-container">
          <div className="features-wrapper">
            {features.map((feature) => (
              <div key={feature.id} className="feature-block">
                <div className="feature-line"></div>
                <div className="feature-icon-wrapper">
                  {feature.icon}
                </div>
                <p className="feature-caption">{feature.caption}</p>
                <h2 className="feature-title">{feature.title}</h2>
                <p className="feature-text">{feature.text}</p>
                <button className="btn-feature" onClick={handleGetStarted}>
                  {feature.button.title}
                  <ArrowRight size={16} />
                </button>
              </div>
            ))}

            <div className="details-grid">
              {details.map((detail) => (
                <div key={detail.id} className="detail-item">
                  <div className="detail-icon">{detail.icon}</div>
                  <span className="detail-title">{detail.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="xora-pricing">
        <div className="pricing-container">
          <div className="pricing-header">
            <h3 className="pricing-title">Simple, Transparent Pricing</h3>
            <p className="pricing-subtitle">Start free. Upgrade when you're ready to automate everything.</p>
            
            <div className="pricing-toggle">
              <button 
                className={`toggle-btn ${monthly ? 'active' : ''}`}
                onClick={() => setMonthly(true)}
              >
                Monthly
              </button>
              <button 
                className={`toggle-btn ${!monthly ? 'active' : ''}`}
                onClick={() => setMonthly(false)}
              >
                Annual
                <span className="save-badge">Save 20%</span>
              </button>
              <div className={`toggle-slider ${!monthly ? 'right' : ''}`}></div>
            </div>
          </div>

          {/* Base Plans */}
          <h4 style={{color: '#888', marginBottom: '20px', textAlign: 'center'}}>Base Plans (Channel Limits)</h4>
          <div className="pricing-grid" style={{gridTemplateColumns: 'repeat(4, 1fr)'}}>
            {plans.map((plan, index) => (
              <div key={plan.id} className={`pricing-card ${index === 0 ? 'featured' : ''}`}>
                {index === 0 && <div className="featured-glow"></div>}
                {index === 0 && <div className="featured-badge">Start Free</div>}
                
                <div className="plan-header">
                  <span className={`plan-title ${index === 0 ? 'highlight' : ''}`}>{plan.title}</span>
                  <div className="plan-price">
                    {plan.priceMonthly === 0 ? (
                      <span className={`price-amount ${index === 0 ? 'highlight' : ''}`}>Free</span>
                    ) : (
                      <>
                        <span className="price-currency">$</span>
                        <span className={`price-amount ${index === 0 ? 'highlight' : ''}`}>
                          {monthly ? plan.priceMonthly : plan.priceYearly}
                        </span>
                        <span className="price-period">/ mo</span>
                      </>
                    )}
                  </div>
                  <p className="plan-caption">{plan.caption}</p>
                </div>

                <ul className="plan-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>
                      <Check size={18} className="check-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  className={`btn-plan ${index === 0 ? 'featured' : ''}`}
                  onClick={handleGetStarted}
                >
                  {index === 0 ? 'Start Free' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>
          
          {/* Tool Add-ons */}
          <div style={{marginTop: '60px'}}>
            <h4 style={{color: '#888', marginBottom: '10px', textAlign: 'center'}}>Power-Up with Tool Add-ons</h4>
            <p style={{color: '#666', textAlign: 'center', marginBottom: '30px'}}>Add AI tools to supercharge your content creation</p>
            
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '30px', maxWidth: '800px', margin: '0 auto'}}>
              {toolAddons.map((tool) => (
                <div key={tool.id} style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  padding: '30px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{fontSize: '2.5rem', marginBottom: '15px'}}>{tool.icon}</div>
                  <h3 style={{fontSize: '1.5rem', marginBottom: '5px'}}>{tool.title}</h3>
                  <p style={{color: '#888', marginBottom: '15px'}}>{tool.description}</p>
                  <div style={{fontSize: '1.8rem', fontWeight: 'bold', color: '#FF9068', marginBottom: '20px'}}>
                    +${tool.price}<span style={{fontSize: '0.9rem', color: '#666'}}>/mo</span>
                  </div>
                  <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                    {tool.features.map((feature, i) => (
                      <li key={i} style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', color: '#aaa'}}>
                        <Check size={16} style={{color: '#FF9068'}} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="xora-faq">
        <div className="faq-container">
          <div className="faq-header">
            <h3 className="faq-title">Questions? We've Got Answers.</h3>
            <p className="faq-subtitle">Everything you need to know about V Lumina AI</p>
          </div>

          <div className="faq-grid">
            <div className="faq-column">
              {faq.slice(0, 3).map((item, index) => (
                <FaqItem 
                  key={item.id} 
                  item={item} 
                  index={index}
                  activeIndex={activeFaq}
                  setActiveIndex={setActiveFaq}
                />
              ))}
            </div>
            <div className="faq-column">
              {faq.slice(3).map((item, index) => (
                <FaqItem 
                  key={item.id} 
                  item={item} 
                  index={index + 3}
                  activeIndex={activeFaq}
                  setActiveIndex={setActiveFaq}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="xora-testimonials">
        <div className="testimonials-container">
          <div className="testimonials-header">
            <p className="testimonials-caption">WALL OF LOVE</p>
            <h3 className="testimonials-title">Creators & Businesses Love Us</h3>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((item) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xora-cta">
        <div className="cta-container">
          <div className="cta-glow"></div>
          <h2 className="cta-title">Ready to Automate Your Content?</h2>
          <p className="cta-text">Join creators and businesses using V Lumina AI to grow their online presence on autopilot.</p>
          <button className="btn-cta" onClick={handleGetStarted}>
            <span>Start Free Trial</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="xora-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <Sparkles className="logo-icon" />
            <span>V Lumina AI</span>
          </div>
          <div className="footer-links">
            <button onClick={() => scrollToSection('features')}>Features</button>
            <button onClick={() => scrollToSection('pricing')}>Pricing</button>
            <button onClick={() => scrollToSection('faq')}>FAQ</button>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
          </div>
          <p className="footer-copy">© 2024 V Lumina AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
